import React from 'react';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { Stack } from '@mui/material';
import { investmentValidationService } from '../../services/InvestmentValidationService';
import { validateInvestment } from '../../services/InvestmentValidator';
import StyledSection from '../styles/StyledSection';
import ToneIconButton from '../styles/ToneIconButton';
import { StyledTypography } from '../styles/styles';
import { FundOpportunityEOIController } from './controller';

export function showLightboxBackground(ctx: FundOpportunityEOIController) {
  ctx.setState({ lightBoxVisible: true });
}

export function closeLightbox(ctx: FundOpportunityEOIController) {
  ctx.setState({
    lightBoxVisible: false,
    requestMessage: false,
    sendMessageError: false,
    sendMessageResult: undefined,
    requestAppointment: false,
    requestShare: false,
  });
}

export function sendMessage(ctx: FundOpportunityEOIController) {
  ctx.showLightboxBackground();
  ctx.setState({ ...ctx.state, ...ctx.resetModalFlags(), requestMessage: true });
}

export function share(ctx: FundOpportunityEOIController) {
  // Navigate to share page instead of showing modal
  window.location.href = `/opportunities/${ctx.state.fundOpportunity!.id}/share`;
}

export function cancelShare(ctx: FundOpportunityEOIController) {
  ctx.closeLightbox();
  ctx.setState({ requestShare: false });
}

export function createAppointment(ctx: FundOpportunityEOIController) {
  ctx.showLightboxBackground();
  ctx.setState({ ...ctx.state, ...ctx.resetModalFlags(), requestAppointment: true });
}

export function submitMessage(ctx: FundOpportunityEOIController, message: string) {
  const delayHide = () => {
    new Promise((resolve) => setTimeout(resolve, 4000)).then(() => {
      ctx.closeLightbox();
    });
  };
  ctx.setState({ sendingMessage: true });
  ctx
    .getBackendService()
    .sendMessage(`New Fund Query - ${ctx.state.fundOpportunity!.name}`, message)
    .then(() => {
      ctx.setState({
        sendingMessage: false,
        sendMessageResult:
          'Thank you for contacting Fides. One of the team will respond to your message shortly via your registered email address.',
      });
      delayHide();
    })
    .catch(() => {
      ctx.setState({
        sendingMessage: false,
        sendMessageResult:
          'Unable to send message, Please try again later. Or contact support@fidescapital.com.au if problem persists',
        sendMessageError: true,
      });
      delayHide();
    });
}

export function handleConfirmEOI(
  ctx: FundOpportunityEOIController,
  e: React.MouseEvent<HTMLInputElement, MouseEvent>,
): void {
  const target = e.target as HTMLInputElement;
  ctx.setState({ eoiConfirmed: target.checked });
}

export function validateIndicativeInvestment(ctx: FundOpportunityEOIController, value?: string) {
  // Use the new async validation service for better accuracy
  investmentValidationService
    .validateInvestment(value)
    .then((result) => {
      ctx.setState({
        indicativeInvestmentError: result.validationError,
        indicativeInvestment: result.validInvestment,
      });
    })
    .catch((error) => {
      console.error('Failed to validate investment:', error);
      // Fall back to synchronous validation if async fails
      const fallbackResult = validateInvestment(value);
      ctx.setState({
        indicativeInvestmentError: fallbackResult.validationError,
        indicativeInvestment: fallbackResult.validInvestment,
      });
    });
}

export function applicationRelevant(ctx: FundOpportunityEOIController): boolean {
  if (ctx.currentStatus() !== 'APPLICATION_OPEN') {
    return false;
  }

  if (!ctx.state.loadedEOI) {
    return false;
  }

  if (!ctx.state.originalEOIIndicativeInvestment) {
    return false;
  }

  if (ctx.state.updateEoi || ctx.state.withdrawEOI) {
    return false;
  }

  return true;
}

export function canRenderApplication(ctx: FundOpportunityEOIController): boolean {
  if (ctx.state.filter !== 'Application' && ctx.state.filter !== 'All') {
    return false;
  }

  return ctx.applicationRelevant();
}

export function renderApplication(ctx: FundOpportunityEOIController): React.ReactNode {
  if (!ctx.canRenderApplication() || ctx.state.printMode) {
    return null;
  }

  const address = ctx.state.fundOpportunity!.addresses?.[0];
  const tone = ctx.props.tone || 'default';
  const loading = ctx.state.loadingApplication;

  return (
    <StyledSection tone={tone} sx={{ mt: 4 }}>
      <Stack spacing={2}>
        <StyledTypography variant="h6" tone={tone}>
          Application
        </StyledTypography>
        <StyledTypography variant="body2" tone={tone}>
          Thank you for expressing interest in the investment opportunity at{' '}
          {address?.streetAddress}, {address?.suburb}. Click below to download the Investment
          Memorandum for this opportunity.
        </StyledTypography>
        <Stack direction="row" justifyContent="flex-start">
          <ToneIconButton
            onClick={() => ctx.downloadApplication()}
            disabled={loading}
            tooltip="Download Investment Memorandum"
          >
            <PictureAsPdfIcon />
          </ToneIconButton>
        </Stack>
      </Stack>
    </StyledSection>
  );
}
